import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import {
  sort,
  formatCurrency,
  getTimezoneDate,
  timezones,
  getUrl,
} from '../../utilities/helpers'
import { flexPackDisplayPrice } from '../../utilities/flexPackPrice'
import {
  resolvePrimaryColor,
  resolveBrandLogo,
} from '../PurchaseFlow/utils/branding'
import authService from '../../utilities/services/auth.service'

import {
  ALL_SPORTS,
  eventSport,
  matchesSport,
  orderedSportLabels,
  packageSport,
  resolveSportSelection,
  sportMatchesQuery,
} from './eventSport'

import './organizationLanding.scss'

const NAVY = '#051b35'

const TABS = [
  { key: 'events', label: 'Upcoming events' },
  { key: 'packages', label: 'Season tickets' },
  { key: 'flex', label: 'Flex packages' },
]

// Query values → internal tab keys, e.g. ?tab=season-tickets
const TAB_QUERY_MAP = {
  'upcoming-events': 'events',
  'season-tickets': 'packages',
  'flex-packs': 'flex',
  events: 'events',
  packages: 'packages',
  flex: 'flex',
}

function tabFromQuery(value) {
  if (!value) return null
  return TAB_QUERY_MAP[String(value).toLowerCase()] || null
}

function resolveTabFromSearchParams(searchParams) {
  const fromTab = tabFromQuery(searchParams.get('tab'))
  if (fromTab) return fromTab

  // Also accept ?upcoming-events / ?season-tickets / ?flex-packs as flags
  for (const [queryKey, tabKey] of Object.entries(TAB_QUERY_MAP)) {
    if (queryKey.includes('-') && searchParams.has(queryKey)) return tabKey
  }
  return 'events'
}

function categoryFromSearchParams(searchParams) {
  return (
    searchParams.get('category') ||
    searchParams.get('sport') ||
    searchParams.get('filter') ||
    ''
  )
}

function tintPrimary(hex, ratio = 0.92) {
  const cleaned = String(hex || NAVY).replace('#', '')
  const full =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned
  const n = parseInt(full, 16)
  if (Number.isNaN(n)) return 'rgba(5, 27, 53, 0.08)'
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  const mix = (c) => Math.round(c + (255 - c) * ratio)
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`
}

function tagFor(status) {
  if (status === 'Few left') return { tagBg: '#fbf1de', tagInk: '#b5791e' }
  if (status === 'Presale') return { tagBg: '#f1f3f8', tagInk: '#4a5567' }
  if (status === 'Sold out') return { tagBg: '#f1f3f8', tagInk: '#6e7180' }
  return { tagBg: '#e6f4eb', tagInk: '#2f8f4e' }
}

function eventStatus(event) {
  if (event?.soldOut) return 'Sold out'
  if (event?.status === 'presale') return 'Presale'
  if (event?.generalOnsale) {
    const onsale = new Date(event.generalOnsale)
    if (!Number.isNaN(onsale.getTime()) && onsale.getTime() > Date.now()) {
      return 'Presale'
    }
  }
  return 'On sale'
}

function promotionalCopy(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function formatCityState(address) {
  if (!address) return ''
  const a = Array.isArray(address) ? address[0] : address
  if (!a) return ''
  const city = (a.city || '').replace(/\b\w/g, (c) => c.toUpperCase())
  const state = (a.state || '').toUpperCase()
  return [city, state].filter(Boolean).join(', ')
}

function BackIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function PinIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

function ShareIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  )
}

export default function OrganizationLanding({
  organization,
  venues = [],
  events: eventsProp = [],
  packages = [],
  flexPacks = [],
  basePath = '',
}) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState(() => resolveTabFromSearchParams(searchParams))
  const [sport, setSport] = useState(ALL_SPORTS)
  const [vw, setVw] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1440
  )
  const loggedIn = authService.isLoggedIn()

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Keep tab in sync when query params change (e.g. shared deep links)
  useEffect(() => {
    setTab(resolveTabFromSearchParams(searchParams))
  }, [searchParams])

  const mobile = vw < 900
  const narrow = !mobile && vw < 1160
  const stacked = mobile || narrow

  const events = sort(eventsProp)
  // timezones() returns a fresh object each call, so memoize it or every
  // downstream memo re-runs on every render.
  const primaryTimezoneAbbr =
    venues?.[0]?.timezone || events?.[0]?.venue?.timezone
  const primaryTimezone = useMemo(
    () => timezones(primaryTimezoneAbbr),
    [primaryTimezoneAbbr]
  )
  const pathPrefix = basePath || `/${organization?.slug}`
  const primaryColor = resolvePrimaryColor(null, organization)
  const logoSrc = resolveBrandLogo(null, organization)
  const primaryTint = tintPrimary(primaryColor)

  const locationLabel =
    formatCityState(organization?.address) ||
    formatCityState(venues?.[0]?.address) ||
    ''

  const kicker =
    organization?.category?.name ||
    (organization?.name?.toLowerCase().includes('athlet')
      ? 'Athletics program'
      : 'Organization')

  const description =
    venues.length > 0
      ? `Tickets for ${organization?.name} — ${venues
          .map((v) => v.name)
          .filter(Boolean)
          .slice(0, 4)
          .join(', ')}${venues.length > 4 ? ', and more' : ''}.`
      : `Browse and buy tickets, season passes, and flex packs for ${organization?.name}.`

  const eventRows = useMemo(() => {
    return events
      .map((event) => {
        const status = eventStatus(event)
        const href = getUrl(
          `/e/${event?.seoUrl}/${event?.shortCode}`,
          event?.seatmap?.ga_only
        )
        const sport = eventSport(event)
        const title = event.name || 'Event'
        const venue = event?.venue?.name || ''
        const promo = promotionalCopy(event?.promotionalDescription)

        if (event?.date_tbd || event?.start_tbd) {
          return {
            key: event.uuid || event.shortCode,
            tbd: true,
            mon: '',
            day: 'TBD',
            dow: '',
            time: '',
            sport,
            title,
            venue,
            promo,
            status,
            sortKey: event?.start
              ? new Date(event.start).getTime()
              : Number.NEGATIVE_INFINITY,
            monthKey: 'tbd',
            monthTitle: 'Date & Time TBD',
            href,
          }
        }

        if (!event?.start) return null
        const tz = timezones(event?.venue?.timezone || primaryTimezone)
        const m = getTimezoneDate(event.start, tz)
        if (!m) return null
        return {
          key: event.uuid || event.shortCode,
          mon: m.format('MMM'),
          day: m.format('D'),
          dow: m.format('ddd'),
          time: event?.time_tbd ? 'Time TBD' : m.format('h:mm A'),
          sport,
          title,
          venue,
          promo,
          status,
          sortKey: m.valueOf(),
          monthKey: m.format('YYYY-MM'),
          monthTitle: m.format('MMMM YYYY'),
          href,
        }
      })
      .filter(Boolean)
  }, [events, primaryTimezone])

  const packageRows = useMemo(
    () =>
      packages.map((pkg) => ({
        pkg,
        sport: packageSport(pkg),
      })),
    [packages]
  )

  // Apply ?category= / ?sport= once the available categories are known.
  // Tracked by value so it seeds the filter without fighting later clicks.
  const appliedCategoryRef = useRef(null)
  useEffect(() => {
    const categoryQuery = categoryFromSearchParams(searchParams)
    if (!categoryQuery) {
      appliedCategoryRef.current = null
      return
    }
    if (appliedCategoryRef.current === categoryQuery) return

    if (
      sportMatchesQuery(ALL_SPORTS, categoryQuery) ||
      categoryQuery.trim().toLowerCase() === 'all'
    ) {
      appliedCategoryRef.current = categoryQuery
      setSport(ALL_SPORTS)
      return
    }

    const sports = orderedSportLabels([...eventRows, ...packageRows])
    const match = resolveSportSelection(sports, categoryQuery)
    // No match yet likely means the storefront is still loading, so leave the
    // ref unset and try again when the data arrives.
    if (!match) return

    appliedCategoryRef.current = categoryQuery
    setSport(match)
  }, [eventRows, packageRows, searchParams])

  const visiblePackages = useMemo(
    () =>
      packageRows
        .filter((row) => matchesSport(row.sport, sport))
        .map((row) => row.pkg),
    [packageRows, sport]
  )

  const { groups, sportFilters } = useMemo(() => {
    const filtered = eventRows
      .filter((e) => matchesSport(e.sport, sport))
      .slice()
      .sort((a, b) => {
        if (a.tbd !== b.tbd) return a.tbd ? -1 : 1
        return a.sortKey - b.sortKey
      })

    const monthKeys = []
    filtered.forEach((e) => {
      if (!monthKeys.includes(e.monthKey)) monthKeys.push(e.monthKey)
    })

    const groups = monthKeys.map((monthKey) => {
      const rows = filtered.filter((e) => e.monthKey === monthKey)
      return {
        title: rows[0]?.monthTitle || monthKey,
        meta: rows.length === 1 ? '1 event' : `${rows.length} events`,
        rows,
      }
    })

    const sports = [
      ALL_SPORTS,
      ...orderedSportLabels([...eventRows, ...packageRows]),
    ]

    // Counts track whichever list the tab is showing, so they always match
    // what the filter would actually reveal.
    const source = tab === 'packages' ? packageRows : eventRows

    const sportFilters = sports
      .map((label) => ({
        label,
        n: source.filter((r) => matchesSport(r.sport, label)).length,
        on: label === sport,
      }))
      .filter((f) => f.n > 0 || f.on)

    return { groups, sportFilters }
  }, [eventRows, packageRows, sport, tab])

  // Flex packs carry no category, so the filter has nothing to act on there.
  const showSportFilters = sportFilters.length > 1 && tab !== 'flex'

  const handleShare = async () => {
    const shareData = {
      title: organization?.name || 'Tickets',
      url: window.location.href,
    }
    try {
      if (navigator.share) {
        await navigator.share(shareData)
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(window.location.href)
      }
    } catch {
      // user cancelled share
    }
  }

  const formatDateRange = (start, end, timezone = primaryTimezone) => {
    const startDate = getTimezoneDate(start, timezone)
    const endDate = getTimezoneDate(end, timezone)
    if (!startDate || !endDate) return ''
    return `${startDate.format('MMM D')} → ${endDate.format('MMM D, YYYY')}`
  }

  if (!organization) {
    return (
      <div className="organization-landing organization-landing--empty">
        <div className="organization-landing__empty-state">
          <h2>Organization not found</h2>
          <p>We couldn&apos;t find tickets for this page.</p>
          <Link to="/browse">Back to browse</Link>
        </div>
      </div>
    )
  }

  const cssVars = {
    '--org-primary': primaryColor,
    '--org-primary-tint': primaryTint,
  }

  return (
    <div className="organization-landing" style={cssVars}>
      <header className="organization-landing__header">
        <div className="organization-landing__header-inner">
          <button
            type="button"
            className="organization-landing__back"
            aria-label="Back"
            onClick={() => {
              if (window.history.length > 1) navigate(-1)
              else navigate('/browse')
            }}
          >
            <BackIcon />
          </button>
          <Link
            to={loggedIn ? '/my-events' : '/login'}
            className="organization-landing__login"
          >
            {loggedIn ? 'My wallet' : 'Log in'}
          </Link>
        </div>
      </header>

      <div
        className={`organization-landing__layout${
          mobile ? ' organization-landing__layout--mobile' : ''
        }${narrow ? ' organization-landing__layout--narrow' : ''}`}
      >
        {!mobile && (
          <aside className="organization-landing__aside">
            <div className="organization-landing__identity">
              <div className="organization-landing__logo-wrap">
                {logoSrc ? (
                  <img src={logoSrc} alt={organization.name} />
                ) : (
                  <span className="organization-landing__logo-fallback">
                    {organization.name?.charAt(0) || 'B'}
                  </span>
                )}
              </div>
              <div className="organization-landing__identity-text">
                <div className="organization-landing__kicker">
                  <span className="organization-landing__kicker-dot" />
                  {kicker}
                </div>
                <h1 className="organization-landing__title">
                  {organization.name}
                </h1>
                {locationLabel && (
                  <div className="organization-landing__location">
                    <PinIcon />
                    {locationLabel}
                  </div>
                )}
              </div>
            </div>

            {showSportFilters && (
              <div className="organization-landing__sport-block">
                <div className="organization-landing__sport-label">Sport</div>
                <div className="organization-landing__sport-list">
                  {sportFilters.map((f) => (
                    <button
                      key={f.label}
                      type="button"
                      className={`organization-landing__sport-btn${
                        f.on ? ' organization-landing__sport-btn--active' : ''
                      }`}
                      onClick={() => setSport(f.label)}
                    >
                      <span>{f.label}</span>
                      <span className="organization-landing__sport-count">
                        {f.n}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className="organization-landing__desc">{description}</p>

            <div className="organization-landing__actions">
              <button
                type="button"
                className="organization-landing__icon-btn"
                aria-label="Share"
                onClick={handleShare}
              >
                <ShareIcon />
              </button>
            </div>
          </aside>
        )}

        <main className="organization-landing__main">
          {mobile && (
            <div className="organization-landing__mobile-identity">
              <div className="organization-landing__logo-wrap organization-landing__logo-wrap--sm">
                {logoSrc ? (
                  <img src={logoSrc} alt={organization.name} />
                ) : (
                  <span className="organization-landing__logo-fallback">
                    {organization.name?.charAt(0) || 'B'}
                  </span>
                )}
              </div>
              <div>
                <div className="organization-landing__kicker organization-landing__kicker--sm">
                  {kicker}
                </div>
                <h1 className="organization-landing__title organization-landing__title--sm">
                  {organization.name}
                </h1>
              </div>
            </div>
          )}

          <div className="organization-landing__tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`organization-landing__tab${
                  tab === t.key ? ' organization-landing__tab--active' : ''
                }`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {mobile && showSportFilters && (
            <div className="organization-landing__sport-chips">
              {sportFilters.map((f) => (
                <button
                  key={f.label}
                  type="button"
                  className={`organization-landing__sport-chip${
                    f.on ? ' organization-landing__sport-chip--active' : ''
                  }`}
                  onClick={() => setSport(f.label)}
                >
                  {f.label}
                  <span>{f.n}</span>
                </button>
              ))}
            </div>
          )}

          {tab === 'events' && (
            <>
              {groups.length === 0 && (
                <div className="organization-landing__empty-card">
                  No upcoming events
                  {sport !== ALL_SPORTS ? ` for ${sport}` : ''}.
                </div>
              )}

              {groups.map((g) => (
                <div key={g.title} className="organization-landing__month">
                  <div className="organization-landing__month-header">
                    <div className="organization-landing__month-title">
                      {g.title}
                    </div>
                    <div className="organization-landing__month-meta">
                      {g.meta}
                    </div>
                    <div className="organization-landing__month-rule" />
                  </div>

                  {g.rows.map((e) => {
                    const soon = e.status === 'Presale'
                    const soldOut = e.status === 'Sold out'
                    const tag = tagFor(e.status)
                    return (
                      <article
                        key={e.key}
                        className="organization-landing__event"
                        onClick={() => {
                          if (!soldOut) navigate(e.href)
                        }}
                        onKeyDown={(ev) => {
                          if (
                            (ev.key === 'Enter' || ev.key === ' ') &&
                            !soldOut
                          ) {
                            ev.preventDefault()
                            navigate(e.href)
                          }
                        }}
                        role="link"
                        tabIndex={0}
                      >
                        <div className="organization-landing__date-box">
                          {e.tbd ? (
                            <div className="organization-landing__date-day">
                              TBD
                            </div>
                          ) : (
                            <>
                              <div className="organization-landing__date-mon">
                                {e.mon}
                              </div>
                              <div className="organization-landing__date-day">
                                {e.day}
                              </div>
                              <div className="organization-landing__date-dow">
                                {e.dow}
                              </div>
                            </>
                          )}
                        </div>

                        <div className="organization-landing__event-info">
                          <div className="organization-landing__event-meta">
                            <span>
                              {e.tbd ? e.sport : `${e.sport} · ${e.time}`}
                            </span>
                            {e.status !== 'On sale' && (
                              <span
                                className="organization-landing__tag"
                                style={{
                                  color: tag.tagInk,
                                  background: tag.tagBg,
                                }}
                              >
                                {e.status}
                              </span>
                            )}
                          </div>
                          <div className="organization-landing__event-title">
                            {e.title}
                          </div>
                          {e.promo && (
                            <p className="organization-landing__event-promo">
                              {e.promo}
                            </p>
                          )}
                          {e.venue && (
                            <div className="organization-landing__event-venue">
                              {e.venue}
                            </div>
                          )}
                        </div>

                        <div
                          className={`organization-landing__event-cta${
                            stacked
                              ? ' organization-landing__event-cta--stacked'
                              : ''
                          }`}
                        >
                          {soon ? (
                            <button
                              type="button"
                              className="organization-landing__btn organization-landing__btn--ghost"
                              onClick={(ev) => {
                                ev.stopPropagation()
                              }}
                            >
                              Remind me
                            </button>
                          ) : soldOut ? (
                            <button
                              type="button"
                              className="organization-landing__btn organization-landing__btn--ghost"
                              disabled
                              onClick={(ev) => ev.stopPropagation()}
                            >
                              Sold out
                            </button>
                          ) : (
                            <Link
                              to={e.href}
                              className="organization-landing__btn organization-landing__btn--primary"
                              onClick={(ev) => ev.stopPropagation()}
                            >
                              Get tickets
                            </Link>
                          )}
                        </div>
                      </article>
                    )
                  })}
                </div>
              ))}
            </>
          )}

          {tab === 'packages' && (
            <div className="organization-landing__list">
              {visiblePackages.length === 0 && (
                <div className="organization-landing__empty-card">
                  No season tickets
                  {sport !== ALL_SPORTS ? ` for ${sport}` : ''} available right
                  now.
                </div>
              )}
              {visiblePackages.map((pkg) => {
                const price =
                  pkg?.pricingTiers?.[0]?.price ?? pkg?.price ?? null
                const tz = timezones(pkg?.venue?.timezone || primaryTimezone)
                return (
                  <article
                    key={pkg.uuid}
                    className="organization-landing__package"
                  >
                    <div className="organization-landing__package-info">
                      <div className="organization-landing__package-kicker">
                        {packageSport(pkg) || 'Season'}
                      </div>
                      <div className="organization-landing__event-title">
                        {pkg.name}
                      </div>
                      <div className="organization-landing__event-venue">
                        {formatDateRange(pkg.start, pkg.end, tz)}
                      </div>
                    </div>
                    <div className="organization-landing__package-cta">
                      {price != null && (
                        <div className="organization-landing__price">
                          <span>From</span>
                          <strong>{formatCurrency(price)}</strong>
                        </div>
                      )}
                      <Link
                        to={`${pathPrefix}/package/${pkg.uuid}`}
                        className="organization-landing__btn organization-landing__btn--primary"
                      >
                        Select
                      </Link>
                    </div>
                  </article>
                )
              })}
            </div>
          )}

          {tab === 'flex' && (
            <div
              className={`organization-landing__flex-grid${
                mobile
                  ? ' organization-landing__flex-grid--mobile'
                  : narrow
                    ? ' organization-landing__flex-grid--narrow'
                    : ''
              }`}
            >
              {flexPacks.length === 0 && (
                <div className="organization-landing__empty-card">
                  No flex packages available right now.
                </div>
              )}
              {flexPacks.map((flexPack) => {
                const count = flexPack?.gameTickets || 0
                const price = flexPackDisplayPrice(flexPack)
                const each = count > 0 ? price / count : null
                const imageUrl = flexPack?.image?.url
                return (
                  <Link
                    key={flexPack.uuid}
                    to={`${pathPrefix}/flex-pack/${flexPack.uuid}`}
                    className="organization-landing__flex-card"
                  >
                    <div
                      className={`organization-landing__flex-media${
                        imageUrl
                          ? ''
                          : ' organization-landing__flex-media--fallback'
                      }`}
                    >
                      {imageUrl ? (
                        <img src={imageUrl} alt="" />
                      ) : (
                        <span className="organization-landing__flex-media-count">
                          {count || '•'}
                        </span>
                      )}
                    </div>
                    <div className="organization-landing__flex-body">
                      <div className="organization-landing__flex-title">
                        {flexPack.name || `${count} vouchers`}
                      </div>
                      {flexPack.name && count > 0 && (
                        <div className="organization-landing__event-venue">
                          {count} vouchers
                        </div>
                      )}
                      <div className="organization-landing__flex-rule" />
                      <div className="organization-landing__flex-row">
                        <span className="organization-landing__flex-price">
                          {formatCurrency(price)}
                        </span>
                        {each != null && (
                          <span className="organization-landing__flex-each">
                            {formatCurrency(each)} each
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
